import { useEffect, useState } from 'react';
import api, { formatApiError, API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Loader2, Shield, FolderOpen } from 'lucide-react';
import OffboardingLettersSection from '../components/OffboardingLettersSection';
import EmployeeDocumentsVault from '../components/EmployeeDocumentsVault';

function fileUrl(fid) {
  const token = localStorage.getItem('hrms_token');
  return `${API}/files/${fid}?auth=${encodeURIComponent(token)}`;
}

export default function Documents() {
  const { user } = useAuth();
  const canManagePolicy = ['admin', 'hr'].includes(user?.role);
  const [policies, setPolicies] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', category: 'personal', file: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, my] = await Promise.all([
      api.get('/documents?category=policy'),
      api.get('/documents?category=personal'),
    ]);
    setPolicies(p.data); setPersonal(my.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.file) return toast.error('Name and file required');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('name', form.name);
      fd.append('category', form.category);
      if (form.description) fd.append('description', form.description);
      await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setOpen(false); setForm({ name: '', description: '', category: 'personal', file: null }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/documents/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const renderCard = (d, canDelete) => (
    <div key={d.id} data-testid={`document-${d.id}`}
         className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className="flex items-start justify-between gap-3">
        <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate">{d.name}</div>
            {d.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{d.description}</div>}
            <div className="text-xs text-slate-400 mt-2">{new Date(d.uploaded_at).toLocaleDateString()} · {d.uploaded_by_name}</div>
          </div>
        </a>
        {canDelete && (
          <button onClick={() => del(d.id, d.name)} data-testid={`delete-doc-${d.id}`}
                  className="text-slate-400 hover:text-red-500 flex-shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Unified documents vault — 5 synced folders (BGC / Employment / Certifications / Reimbursements / Misc) */}
      {user?.id && (
        <EmployeeDocumentsVault employeeId={user.id} isSelf={true} canManage={false} />
      )}

      {/* Offboarding letters surface here once the employee is terminated */}
      {user?.terminated && (
        <OffboardingLettersSection employeeId={user.id} employeeName={user.name} terminated={true} />
      )}

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Library</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Documents</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-doc-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
              <Upload className="w-4 h-4 mr-2" /> Upload document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="upload-doc-dialog">
            <DialogHeader><DialogTitle className="font-heading">Upload document</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Category</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button onClick={() => setForm({...form, category: 'personal'})} data-testid="doc-cat-personal"
                          className={`p-3 rounded-xl border text-sm font-medium transition-colors ${form.category === 'personal' ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                    Personal
                  </button>
                  <button onClick={() => setForm({...form, category: 'policy'})} data-testid="doc-cat-policy"
                          disabled={!canManagePolicy}
                          className={`p-3 rounded-xl border text-sm font-medium transition-colors ${form.category === 'policy' ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : 'bg-white border-slate-200 hover:border-slate-300'} disabled:opacity-50`}>
                    Org policy {!canManagePolicy && '(admin/HR)'}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Document name</Label>
                <Input data-testid="doc-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Description (optional)</Label>
                <Textarea data-testid="doc-description" rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">File</Label>
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" data-testid="doc-file"
                       onChange={(e) => setForm({...form, file: e.target.files?.[0]})}
                       className="block w-full mt-1 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#3D4A8E] file:text-white file:px-4 file:py-2 file:text-sm file:font-medium" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={submit} disabled={busy || !form.name || !form.file} data-testid="submit-doc" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="policy">
        <TabsList className="rounded-full bg-slate-100 p-1 h-auto">
          <TabsTrigger value="policy" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-policies">
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Org policies
          </TabsTrigger>
          <TabsTrigger value="personal" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-personal-docs">
            <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> My documents
          </TabsTrigger>
        </TabsList>
        <TabsContent value="policy" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {policies.map(d => renderCard(d, canManagePolicy))}
            {policies.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-400">No policy documents yet.</div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="personal" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {personal.map(d => renderCard(d, true))}
            {personal.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-400">No personal documents yet.</div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
